import React, { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify"; // Importar toast

interface Module {
  fileKey: string;
}

interface FileDropZoneProps {
  module: Module;
  handleFileDrop: (e: React.DragEvent<HTMLDivElement>, fileKey: string) => void;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    fileKey: string
  ) => void;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  module,
  handleFileDrop,
  handleFileChange,
}) => {
  const [isDragging, setIsDragging] = useState(false); // Estado para saber si el archivo está en la zona
  const dropZoneRef = useRef<HTMLDivElement | null>(null); // Ref para la zona de arrastre

  // Verifica si el archivo es PDF o Word
  const isValidFile = (file: File): boolean => {
    return (
      file.type === "application/pdf" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );
  };

  // Maneja cuando el archivo entra en la zona
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Evento 'dragenter' detectado.");
    setIsDragging(true); // Establece que el archivo está dentro de la zona
  }, []);

  // Maneja el movimiento del archivo sobre la zona
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Evento 'dragover' detectado.");
  }, []);

  // Maneja cuando el archivo sale de la zona
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const relatedTarget = e.relatedTarget as HTMLElement;

    if (relatedTarget && !dropZone.contains(relatedTarget)) {
      console.log("Evento 'dragleave' detectado.");
      setIsDragging(false); // Restablece el estado cuando el archivo sale de la zona
    }
  }, []);

  // Maneja el evento de soltado de un archivo
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, fileKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Archivo soltado en la zona.");
    setIsDragging(false); // Restablece el estado después de soltar el archivo

    const files = e.dataTransfer ? e.dataTransfer.files : undefined;

    if (files && files.length > 0) {
      const file = files[0];
      if (isValidFile(file)) {
        console.log("Archivo recibido con éxito:", file);
        handleFileChange(
          { target: { files } } as React.ChangeEvent<HTMLInputElement>,
          fileKey
        );
      } else {
        toast.error("¡Solo se permiten archivos PDF o Word (.docx)!");
      }
    } else {
      console.warn("No se detectaron archivos en el evento 'drop'.");
      toast.error("¡Intenta arrastrar el archivo de nuevo!");
    }
  };

  // Maneja el clic en la zona de arrastre
  const handleClickZone = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("Zona clickeada.");
    const fileInput = document.getElementById(
      `file-input-${module.fileKey}`
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <div
      ref={dropZoneRef}
      className={`border-2 border-dashed text-gray-600 cursor-pointer hover:bg-gray-100 transition-all duration-200 ${
        isDragging ? "border-blue-500 bg-blue-50" : "border-gray-400"
      }`}
      onDrop={(e) => handleDrop(e, module.fileKey)}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      aria-label="Arrastra y suelta un archivo"
      role="button"
      tabIndex={0}
      style={{
        height: "112px",
        minHeight: "112px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        flexDirection: "column",
      }}
      onClick={handleClickZone}
    >
      {isDragging ? (
        <p className="text-sm mb-0">
          <span className="text-blue-500">¡Suéltalo!</span>
        </p>
      ) : (
        <p className="text-sm mb-0">
          <>
            Arrastra y suelta un archivo <br /> o <br />
            <span
              className="text-blue-500 underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleClickZone(e);
              }}
            >
              selecciona uno haciendo click.
            </span>
          </>
        </p>
      )}

      <input
        type="file"
        onChange={(e) => {
          console.log("Archivo seleccionado manualmente.");
          const files = e.target.files;
          if (files && files.length > 0 && isValidFile(files[0])) {
            handleFileChange(e, module.fileKey);
          } else {
            // Muestra un mensaje de error si el archivo no es PDF o Word
            toast.error("¡Solo se permiten archivos PDF o Word (.docx)!");
          }
        }}
        className="hidden"
        id={`file-input-${module.fileKey}`}
        accept=".pdf,.docx" // Acepta archivos PDF y Word
      />
    </div>
  );
};

export default FileDropZone;